// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { Icon } from '@lmc/ui-kit'
import { Alert } from '@lmc/ui-kit/src/feedback'
import { BackButton, Button } from '@lmc/ui-kit/src/general'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import AnimateHeight from 'react-animate-height'
import Flatpickr from 'react-flatpickr'
import { connect } from 'react-redux'
import Select from 'react-select'
import { getNominative } from '../../common/utils'
import Selectors from '../../selectors'
import { CurrentTodoSummary, Dot } from '../generic'
import Modal from '.'
import { ModalTitleSelector, TodoAssigneeSelector } from './components'

/** Id of the full-screen container this screen is mounted in (ManageTasksTable). */
export const EDIT_SCREEN_CONTAINER_ID = 'todo-edit-screen-container'

export class TodoEditScreen extends Component {
  state = {
    formErrors: [],
    isSubmitting: false
  }

  // The screen scrolls inside its own fixed container, so scrolling the window
  // would no longer bring the error alert back into view.
  scrollToTop = () => {
    document.getElementById(EDIT_SCREEN_CONTAINER_ID)?.scrollTo?.(0, 0)
  }

  toggleRecurring = () => {
    const {
      setFormField,
      fields: { recurrence }
    } = this.props
    const value = recurrence === 'once' ? 'daily' : 'once'
    setFormField({
      key: 'recurrence',
      value
    })
  }

  handleTimeChange = (newDate) => {
    const {
      setFormField,
      fields: { startDate }
    } = this.props
    const d = dayjs(newDate[0])
    setFormField({
      key: 'startDate',
      value: dayjs(startDate)
        .set('hour', d.get('hour'))
        .set('minute', d.get('minute'))
        .valueOf()
    })
  }

  handleDateChange = (newDate) => {
    const {
      setFormField,
      fields: { startDate }
    } = this.props
    const d = dayjs(newDate[0])
    const startDateValue = dayjs(startDate)
    setFormField({
      key: 'startDate',
      value: d
        .set('hour', startDateValue.get('hour'))
        .set('minute', startDateValue.get('minute'))
        .valueOf()
    })
  }

  handleRecurrenceChange = ({ value }) => {
    this.props.setFormField({
      key: 'recurrence',
      value
    })
  }

  handleSubmit = () => {
    this.setState({ formErrors: [], isSubmitting: true })

    const {
      fields: { id, title, resident, assignee },
      preferredPeopleNominative,
      updateItem
    } = this.props

    const formErrors = []
    if (!title) formErrors.push('Title is required.')
    if (!assignee.length && !resident.length) {
      formErrors.push(
        `Please select at least one ${getNominative(
          preferredPeopleNominative,
          'resident'
        )} or Carer.`
      )
    }

    if (formErrors.length) {
      this.setState({ formErrors, isSubmitting: false })
      return this.scrollToTop()
    }

    updateItem({
      url: `list/recurring-tasks/${id}`,
      onComplete: () => {
        this.setState({ isSubmitting: false })
        this.props.onComplete()
      },
      onError: () => {
        this.setState({ isSubmitting: false })
        this.scrollToTop()
      }
    })
  }

  renderErrors = () => {
    const { errors } = this.props
    const { formErrors } = this.state
    const chosenErrors = (formErrors.length ? formErrors : errors) || []
    const hasErrors = !!(formErrors.length || (errors && errors.length))
    const errorMessageCombined = chosenErrors.map((error, i) => (
      <div key={i}>{error}</div>
    ))

    return (
      hasErrors && (
        <AnimateHeight
          animateOpacity
          duration={200}
          height={hasErrors ? 'auto' : 0}
        >
          <Alert
            key="error"
            type="error"
            message={errorMessageCombined}
          ></Alert>
        </AnimateHeight>
      )
    )
  }

  renderTimePicker = () => {
    const { startDate } = this.props.fields
    if (!startDate) return null

    const currentDate = dayjs(startDate).toDate()

    const date_options = {
      dateFormat: 'd M',
      defaultDate: currentDate
    }

    const time_options = {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      defaultDate: currentDate,
      time_24hr: true
    }

    return (
      <div style={styles.timeInputContainer}>
        <Flatpickr
          style={styles.input}
          options={date_options}
          onChange={this.handleDateChange}
        />
        <span style={{ margin: 20, fontSize: 16 }}>at:</span>
        <Flatpickr
          style={styles.input}
          options={time_options}
          onChange={this.handleTimeChange}
        />
      </div>
    )
  }

  renderRecurringSelector = () => {
    const { recurrence } = this.props.fields
    const recTypes = [
      { value: 'once', label: 'Once' },
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'bi-weekly', label: 'Bi-Weekly' },
      { value: 'monthly', label: 'Monthly' }
    ]
    return (
      <AnimateHeight
        duration={500}
        height={recurrence !== 'once' ? 'auto' : 0}
        animateOpacity
      >
        <div style={styles.recurrenceSelector}>
          <div style={{ marginTop: 30 }}>
            <Select
              classNamePrefix="lmc-select"
              value={recTypes.filter((type) => type.value === recurrence)}
              options={recTypes}
              onChange={this.handleRecurrenceChange}
            />
          </div>
        </div>
      </AnimateHeight>
    )
  }

  renderRecurrenceToggle = () => {
    const isRecurring = this.props.fields.recurrence !== 'once'
    return (
      <div
        className="lmc-radio-toggle-button"
        style={styles.toggleButton}
        onClick={this.toggleRecurring}
      >
        <Dot
          label={
            isRecurring ? (
              <Icon name="CheckOutlined" />
            ) : (
              <Icon name="CloseOutlined" />
            )
          }
          active={isRecurring}
          color={colors.green}
        />{' '}
        Recurring?
      </div>
    )
  }

  render() {
    const {
      carers,
      close,
      residents,
      features,
      todoSummary,
      fields,
      setFormField,
      initial
    } = this.props

    if (!carers || !residents || !fields) return null

    const isDirty = !isEqual(fields, initial)

    return (
      <div style={styles.container}>
        <ConfirmationModal shouldPrompt={isDirty}>
          <BackButton onClick={close} id="close-edit-screen" />
        </ConfirmationModal>
        <NavigationModal canNavigate={!isDirty} />
        <div style={styles.sectionContainer}>
          {this.renderErrors()}
          <CurrentTodoSummary todoSummary={todoSummary} />
          <ModalTitleSelector
            formId={FORM_ID || 'TETS'}
            book="123123"
            fields={fields}
            setFormField={setFormField}
            features={features}
          />
          <div style={styles.fieldLabel}>Start Date / Time</div>
          {this.renderTimePicker()}
          <div style={{ paddingBottom: 50 }}>
            {this.renderRecurrenceToggle()}
            {this.renderRecurringSelector()}
          </div>
          <span style={styles.fieldLabel}>Who's it for?</span>
          <TodoAssigneeSelector
            data={{ carers, residents }}
            features={features}
            leftAlign
            noTitle
            fields={fields}
            setFormField={setFormField}
          />
        </div>
        <div className="lmc-sticky-footer" style={styles.saveContainer}>
          <div style={styles.saveMessage}>
            Note: these changes will only apply to future to-do's.
          </div>
          <Button
            variant="primary"
            onClick={this.handleSubmit}
            disabled={this.state.isSubmitting}
          >
            {this.state.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    )
  }
}

const styles = {
  backButton: {
    bottom: 10,
    paddingLeft: 0,
    paddingRight: 0,
    position: 'relative'
  },
  button: {
    background: colors.successgreen,
    color: 'white',
    fontSize: 13,
    marginBottom: 5,
    padding: '0px 25px',
    textShadow: 'none',
    width: 100
  },
  container: {
    padding: '20px 20px 0px 20px',
    margin: '0 auto',
    maxWidth: 1000
  },
  fieldLabel: {
    fontWeight: 'bold',
    marginBottom: 10
  },
  sectionContainer: {
    margin: '0 auto',
    maxWidth: 400
  },
  timeInputContainer: {
    paddingBottom: 30
  },
  input: {
    textAlign: 'center',
    border: `1px solid ${colors.grey}`,
    borderRadius: 4,
    width: 100,
    letterSpacing: 1,
    padding: '8px 4px'
  },
  saveContainer: {
    background: 'white',
    opacity: 0.9,
    boxShadow: 'none',
    marginTop: 50
  },
  saveMessage: {
    fontSize: 13,
    maxWidth: 220,
    opacity: 0.7,
    paddingLeft: 20
  },
  toggleButton: {
    fontWeight: 'bold',
    width: 170,
    cursor: 'pointer',
    position: 'relative',
    right: 6,
    top: 13
  },
  recurrenceSelector: {
    maxWidth: 420,
    position: 'relative',
    top: -12
  },
  timeOptions: {
    padding: 20
  },
  selectRecType: {
    position: 'relative',
    width: 180
  }
}

TodoEditScreen.propTypes = {
  carers: PropTypes.array,
  residents: PropTypes.array
}

const mapStateToProps = (state, props) => ({
  carers: state.data.carers,
  features: state.global.home,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  residents: state.data.residents,
  todoSummary: Selectors.getTodoSummary(state, props)
})

const wrappedModal = connect(mapStateToProps, null)(TodoEditScreen)
const FORM_ID = 'editTask'
export default Modal(wrappedModal, FORM_ID)
